import { AccountWorldValue } from './account-world';

/**
 * Raw shape of the upstream PFM API response.
 *
 * A feature may live in either `accountLevel` or `channelLevel`. `accountWorld` is never present
 * on the wire — the service derives it from the account's `contractType` (V2 companies API) before
 * caching the payload. Use this type only when representing the wire payload directly.
 */
export interface RawProductFeatureData {
  /**
   * Currently expected to be empty / absent — features live in `channelLevel`. Retained in the
   * contract; may be repopulated in a future iteration.
   */
  accountLevel?: FeatureLevel;
  channelLevel?: ChannelFeatureLevel[];
}

export interface ProductFeatureData {
  /**
   * Guaranteed to be present (normalised to `{ features: [] }` when absent on the wire).
   *
   * A feature may live in either `accountLevel` or `channelLevel`. `hasFeature()` checks
   * `channelLevel` first and falls back to `accountLevel`, so prefer `hasFeature()` over reading
   * this directly.
   */
  accountLevel: FeatureLevel;
  /**
   * Guaranteed to be present (normalised to `[]` when absent on the wire).
   *
   * Holds per-channel features. `hasFeature()` checks `channelLevel` first — a single `channelId`,
   * or the account-wide combination of channels per `channelMatch` (default intersection; `Any` for
   * union) — before falling back to `accountLevel`. Prefer `hasFeature()`.
   */
  channelLevel: ChannelFeatureLevel[];
  /**
   * Which "world" this account's contract belongs to. Always set on the value returned by
   * `getFeatures()` (`UNKNOWN` when the contract could not be determined).
   *
   * Never present in the upstream PFM response — the service derives it from the account's
   * `contractType` (V2 companies API).
   *
   * Metadata only — it does **not** affect `hasFeature()` resolution (which checks `channelLevel`
   * then `accountLevel` regardless of account type).
   *
   * - `AccountWorld.NewWorld` (`'NEW_WORLD'`) → `contractType` ∈ NONE…THIRD_PARTY.
   * - `AccountWorld.Legacy`   (`'LEGACY'`)    → migrated / legacy (`contractType` = LEGACY).
   * - `AccountWorld.Unknown`  (`'UNKNOWN'`)   → company fetch failed or no `contractType`.
   */
  accountWorld: AccountWorldValue;
}

export interface FeatureLevel {
  features: FeatureItem[];
}

export interface ChannelFeatureLevel {
  channelId: string;
  features: FeatureItem[];
}

export interface FeatureItem {
  name: string;
  properties: FeatureProperty[];
}

export interface FeatureProperty {
  propertyName: string;
  value: string;
}
